import React, { useEffect, useState, memo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  TextField,
  InputAdornment,
  ListSubheader,
} from '@mui/material';
import { SearchNormal1 } from 'iconsax-react';
import ApiContext from 'contexts/ApiContext';

interface EscalationBlockProps {
  type: 'videoCall' | 'chat';
  value: any;
  onChange: (type: 'videoCall' | 'chat', value: any) => void;
  roles: { uid: string; roleName: string }[];
  users: { uid: string; name: string; role?: string }[];
  loadingRoles: boolean;
  loadingUsers: boolean;
}

export const EscalationBlock: React.FC<EscalationBlockProps> = ({ type, value, onChange, roles, users, loadingRoles, loadingUsers }) => {
  // State for search functionality
  const [roleSearchText, setRoleSearchText] = useState('');
  const [userSearchText, setUserSearchText] = useState('');
  // State for escalation type selection - start with empty value
  const [escalationTo, setEscalationTo] = useState<'users' | 'actors' | ''>('');

  // Ensure actors and users are always arrays of strings (UIDs)
  const normalizeToUIDs = (items: any[]): string[] => {
    if (!Array.isArray(items)) return [];
    return items.map(item => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && item?.uid) return item.uid;
      if (typeof item === 'object' && item?.id) return item.id;
      return String(item);
    }).filter(Boolean);
  };

  const currentActors = normalizeToUIDs(value.actors || []);
  const currentUsers = normalizeToUIDs(value.users || []);

  // Filter roles based on search text
  const filteredRoles = roles.filter(role =>
    role.roleName.toLowerCase().includes(roleSearchText.toLowerCase())
  );

  // Filter users based on search text (role included: the same person can be
  // listed under more than one role, and the role is how you tell them apart)
  const filteredUsers = users.filter(user =>
    `${user.name} ${user.role || ''}`.toLowerCase().includes(userSearchText.toLowerCase())
  );

  // Set initial escalation type based on existing data only
  React.useEffect(() => {
    if (currentActors.length > 0 && currentUsers.length === 0) {
      setEscalationTo('actors');
    } else if (currentUsers.length > 0 && currentActors.length === 0) {
      setEscalationTo('users');
    }
    // If both are empty or both have values, keep the current state (empty initially)
  }, [currentActors.length, currentUsers.length]);

  // Memoized handler for escalation type change
  const handleEscalationTypeChange = useCallback((newEscalationType: 'users' | 'actors') => {
    // Use flushSync to ensure synchronous updates
    flushSync(() => {
      setEscalationTo(newEscalationType);
    });
    
    // Then update the data
    const updatedValue = { ...value };
    console.log('EscalationBlock: Changing escalation type to', newEscalationType, updatedValue);

    if (newEscalationType === 'users') {
      updatedValue.actors = [];
    } else {
      updatedValue.users = [];
    }
    
    onChange(type, updatedValue);

  }, [type, value, onChange]);

  return (
    <>
      {/* Escalation To dropdown */}
      <Box sx={{ my: 1 }}>
        <Typography sx={{ mb: 1 }}>Escalation to</Typography>
        <FormControl fullWidth>
          <Select
            value={escalationTo}
            onChange={(e) => handleEscalationTypeChange(e.target.value as 'users' | 'actors')}
            displayEmpty
          >
            <MenuItem value="" disabled>
              <em>Select escalation type</em>
            </MenuItem>
            <MenuItem value="users">Users</MenuItem>
            <MenuItem value="actors">Actors (Roles)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Conditionally render based on escalation type */}
      {escalationTo === 'actors' && (
        <Box sx={{ my: 1 }}>
          <Typography sx={{ mb: 1 }}>Actors (Roles)</Typography>
          <FormControl fullWidth>
            <Select
              multiple
              value={currentActors}
              onChange={e => {
                const selectedUIDs = Array.isArray(e.target.value) ? e.target.value : [];
                // console.log('Actors onChange - selected UIDs:', selectedUIDs);
                onChange(type, { ...value, actors: selectedUIDs });
              }}
              renderValue={selected => {
                if (loadingRoles) return 'Loading roles...';
                if (!selected || (selected as string[]).length === 0) return 'Select roles';
                
                const selectedArray = selected as string[];
                console.log('Selected roles array:', selectedArray);
                console.log('Available roles:', roles);
                
                const displayNames = selectedArray.map(uid => {
                  const role = roles.find(r => r.uid === uid);
                  const displayName = role?.roleName || uid;
                  console.log(`Role mapping: ${uid} -> ${displayName}`);
                  return displayName;
                });
                
                return displayNames.join(', ');
              }}
              displayEmpty
              MenuProps={{
                disableAutoFocusItem: true,
                PaperProps: {
                  style: {
                    maxHeight: 300, // Fixed height for consistent UI
                  },
                },
              }}
            >
              <ListSubheader>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Search roles..."
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchNormal1 size="16" />
                      </InputAdornment>
                    ),
                  }}
                  value={roleSearchText}
                  onChange={(e) => setRoleSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    // just stop propagation; leave default so the input receives the character
                    if (e.key !== 'Escape') {
                      e.stopPropagation();
                    }
                  }}
                />
              </ListSubheader>
              {loadingRoles ? (
                <MenuItem disabled>Loading roles...</MenuItem>
              ) : filteredRoles.length === 0 ? (
                <MenuItem disabled>
                  {roleSearchText ? 'No roles found matching search' : 'No roles found'}
                </MenuItem>
              ) : (
                filteredRoles.map(role => (
                  <MenuItem key={role.uid} value={role.uid}>
                    {role.roleName}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>
      )}

      {escalationTo === 'users' && (
        <Box sx={{ my: 1 }}>
          <Typography sx={{ mb: 1 }}>Users</Typography>
          <FormControl fullWidth>
            <Select
              multiple
              value={currentUsers}
              onChange={e => {
                const selectedUIDs = Array.isArray(e.target.value) ? e.target.value : [];
                console.log('Users onChange - selected UIDs:', selectedUIDs);
                onChange(type, { ...value, users: selectedUIDs });
              }}
              renderValue={selected => {
                if (loadingUsers) return 'Loading users...';
                if (!selected || (selected as string[]).length === 0) return 'Select users';
                
                const selectedArray = selected as string[];
                // console.log('Selected users array:', selectedArray);
                // console.log('Available users:', users);
                
                const displayNames = selectedArray.map(uid => {
                  const user = users.find(u => u.uid === uid);
                  if (!user) return uid;
                  return user.role ? `${user.name} - ${user.role}` : user.name;
                });
                
                return displayNames.join(', ');
              }}
              displayEmpty
              MenuProps={{
                disableAutoFocusItem: true,
                PaperProps: {
                  style: {
                    maxHeight: 300, // Fixed height for consistent UI
                  },
                },
              }}
            >
              <ListSubheader>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Search users..."
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchNormal1 size="16" />
                      </InputAdornment>
                    ),
                  }}
                  value={userSearchText}
                  onChange={(e) => setUserSearchText(e.target.value)}
                  onKeyDown={(e) => { 
                    // still allows the keystroke to be entered into the field.
                    if (e.key !== 'Escape') {
                      e.stopPropagation();

                    }
                  }}
                />
              </ListSubheader>
              {loadingUsers ? (
                <MenuItem disabled>Loading users...</MenuItem>
              ) : filteredUsers.length === 0 ? (
                <MenuItem disabled>
                  {userSearchText ? 'No users found matching search' : 'No users found'}
                </MenuItem>
              ) : (
                filteredUsers.map(user => (
                  <MenuItem key={user.uid} value={user.uid} sx={{ whiteSpace: 'normal' }}>
                    {user.name}
                    {user.role && (
                      <Typography component="span" sx={{ ml: 0.5, fontSize: '0.75rem', fontWeight: 400 }}>
                        - {user.role}
                      </Typography>
                    )}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>
      )}

    </>
  );
};

interface EscalationSettingsProps {
  selectedNode: any;
  escalationPayload?: any;
  onEscalationChange?: (payload: any) => void;
}

const EscalationSettings: React.FC<EscalationSettingsProps> = ({ selectedNode, escalationPayload, onEscalationChange }) => {
  const api = React.useContext(ApiContext);
  const [roles, setRoles] = useState<{ uid: string; roleName: string }[]>([]);
  const [users, setUsers] = useState<{ uid: string; name: string; role?: string }[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Extract clientUid
  const clientObject = selectedNode?.data?.client || selectedNode?.data?.step?.client;
  const clientUid = clientObject?.uid || clientObject;
  const teamObject = selectedNode?.data?.team || selectedNode?.data?.step?.team;
  const teamUid = teamObject?.uid || teamObject;

  // Fetch roles
  useEffect(() => {
    const fetchRoles = async (clientUid: any) => {
      if (!api || !clientUid) return;
      setLoadingRoles(true);
      try {
        const res = await api.getResourceTypes(clientUid, 'user');
        const roleData = res?.data; 
        if (Array.isArray(roleData)) { 
          const mappedRoles = roleData.map((role: any) => ({ 
            uid: role.uid, 
            roleName: role.name || role.roleName || role.title || 'Unnamed Role'
          }));
          // console.log('Mapped roles:', mappedRoles);
          setRoles(mappedRoles);
        } else {
          setRoles([]);
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        setRoles([]);
      } finally {
        setLoadingRoles(false);
      }
    };
    if (clientUid) fetchRoles(clientUid);
  }, [api, clientUid]);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      if (!teamUid || !api) return;
      setLoadingUsers(true);
      try {
        const res = await api.getTeamUsers(teamUid);
        const userList = res?.data; 
        if (Array.isArray(userList)) {
          // console.log('First user item:', userList[0]);
          const mappedUsers = userList
            .map((entry: any) => {
              let name = '';
              let uid = '';
              let role = '';

              // Handle different possible user data structures
              const source = entry.user || (entry.uid ? entry : null);
              if (source) {
                uid = source.uid;
                const firstName = source.firstName || '';
                const lastName = source.lastName || '';
                name = `${firstName} ${lastName}`.trim() || source.email || source.username || '';
                // Same person can hold more than one role (e.g. VADS and Ops),
                // so the role is what distinguishes the entries in the picker.
                role = source.role?.roleName || source.role?.name || (typeof source.role === 'string' ? source.role : '');
              }

              return { uid, name, role };
            })
              .filter(user => user.uid && user.name && user.name.trim() !== '' && user.name !== 'Unnamed User');
            
            // console.log('Mapped users:', mappedUsers);
          setUsers(mappedUsers);
        } else {
          setUsers([]);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, [teamUid, api]);

  // Process-level default, for the read-only preview when inheriting.
  useEffect(() => {
    let active = true;
    (async () => {
      if (!api || !teamUid) return;
      try {
        const res = await api.getEscalationDefault(teamUid);
        if (active) setProcessDefault(res?.data ?? res ?? null);
      } catch {
        if (active) setProcessDefault(null);
      }
    })();
    return () => { active = false; };
  }, [api, teamUid]);

  // Controlled escalation payload
  const [localValue, setLocalValue] = useState<any>({
    videoCall: { enabled: false, actors: [], users: [], inherit: true },
    chat: { enabled: false, actors: [], users: [] }
  });

  // The process default, shown read-only while this step is inheriting so the
  // configurer can see what the step will actually do.
  const [processDefault, setProcessDefault] = useState<any>(null);

  // Sync with parent - using JSON.stringify for deep comparison
  useEffect(() => {
    // Helper function to normalize arrays to UIDs
    const normalizeToUIDs = (items: any[]): string[] => {
      if (!Array.isArray(items)) return [];
      return items.map(item => {
        if (typeof item === 'string') return item;
        if (typeof item === 'object' && item?.uid) return item.uid;
        if (typeof item === 'object' && item?.id) return item.id;
        return String(item);
      }).filter(Boolean);
    };

    // Always set the localValue, even if escalationPayload is null/undefined
    // Mirror the backend's derivation exactly (escalation-resolver.ts), or the
    // drawer and the server disagree about what a legacy step is doing.
    const rawInherit = escalationPayload?.videoCall?.inherit;
    const derivedInherit = rawInherit === undefined || rawInherit === null
      // Either mode being switched on counts as deliberate configuration.
      ? escalationPayload?.videoCall?.enabled !== true && escalationPayload?.chat?.enabled !== true
      : rawInherit === true;

    const newLocalValue = {
      videoCall: {
        enabled: escalationPayload?.videoCall?.enabled || false,
        callType: escalationPayload?.videoCall?.callType || 'VIDEO',
        inherit: derivedInherit,
        // Ensure actors and users are normalized to UIDs
        actors: normalizeToUIDs(escalationPayload?.videoCall?.actors || []),
        users: normalizeToUIDs(escalationPayload?.videoCall?.users || [])
      },
      chat: {
        enabled: escalationPayload?.chat?.enabled || false,
        // Ensure actors and users are normalized to UIDs
        actors: normalizeToUIDs(escalationPayload?.chat?.actors || []),
        users: normalizeToUIDs(escalationPayload?.chat?.users || [])
      }
    };
    
    // console.log('EscalationSettings: Normalized localValue:', newLocalValue);
    setLocalValue(newLocalValue);
  }, [JSON.stringify(escalationPayload), roles.length, users.length]);

  // Notify parent on change
  const handleBlockChange = (type: 'videoCall' | 'chat', blockValue: any) => {
    // Ensure actors and users are always arrays of UIDs
    const normalizedBlockValue = {
      ...blockValue,
      actors: Array.isArray(blockValue.actors) ? blockValue.actors : [],
      users: Array.isArray(blockValue.users) ? blockValue.users : []
    };
    
    const updated = { ...localValue, [type]: normalizedBlockValue };
    // console.log('EscalationSettings: Block change normalized:', type, normalizedBlockValue);
    // console.log('EscalationSettings: Full updated payload:', updated);
    setLocalValue(updated);
    if (onEscalationChange) {
      onEscalationChange(updated);
    }
  };

  const inheriting = localValue.videoCall.inherit !== false;

  const describeMode = (cfg: any, label: string) => {
    if (!cfg?.enabled) return null;
    // Naming people turns the role list off — say what will actually happen.
    const target = cfg.users?.length
      ? `${cfg.users.length} named user(s)`
      : `${cfg.actors?.length || 0} role(s)`;
    return `${label} → ${target}`;
  };

  const describeDefault = () => {
    if (!processDefault) return 'Loading the process default…';
    const parts = [
      describeMode(processDefault.videoCall, 'Call'),
      describeMode(processDefault.chat, 'Chat'),
    ].filter(Boolean);
    return parts.length ? parts.join('  ·  ') : 'Escalation is switched off for this process.';
  };

  return (
    <Box sx={{ my: 0, p: 0 }}>
      {/* Inherit vs override. Steps follow the process default unless someone
          deliberately configures them, so a new step is reachable immediately
          and there is no 20-steps-to-configure problem. */}
      <RadioGroup
        value={inheriting ? 'inherit' : 'own'}
        onChange={e => handleBlockChange('videoCall', { ...localValue.videoCall, inherit: e.target.value === 'inherit' })}
        sx={{ mb: 1 }}
      >
        <FormControlLabel value="inherit" control={<Radio size="small" />} label="Use process default" />
        <FormControlLabel value="own" control={<Radio size="small" />} label="Configure for this step" />
      </RadioGroup>

      {inheriting && (
        <Box sx={{ ml: 2, mb: 1, p: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            {describeDefault()}
          </Typography>
        </Box>
      )}

      {/* Call and chat are configured independently: the people you want on a
          video call are often not the people you want in a chat thread. Each
          block's own switch is also what says whether that mode is offered. */}
      {!inheriting && (['videoCall', 'chat'] as const).map(mode => (
        <Box key={mode} sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!localValue[mode].enabled}
                onChange={e => handleBlockChange(mode, { ...localValue[mode], enabled: e.target.checked })}
              />
            }
            label={mode === 'videoCall' ? 'Enable Call' : 'Enable Chat'}
          />
          {localValue[mode].enabled && (
            <Box sx={{ ml: 2 }}>
              <EscalationBlock
                type={mode}
                value={localValue[mode]}
                onChange={handleBlockChange}
                roles={roles}
                users={users}
                loadingRoles={loadingRoles}
                loadingUsers={loadingUsers}
              />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default memo(EscalationSettings);
